import React, { useEffect, useMemo, useRef, useState } from "react";
import { academicHeritageApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import "./AcademicHeritage.scss";

const defaultForm = {
  _id: null,
  Name: "",
  Title: "",
  Org: "Rajat Group of Colleges, Lucknow/Ambedkar Nagar, U.P",
  Display_Order: 0,
  Photo: ""
};

const AcademicHeritage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [modalType, setModalType] = useState(""); // "add", "edit"
  const [formData, setFormData] = useState(defaultForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchHeritageItems();
  }, []);

  const fetchHeritageItems = async () => {
    try {
      setLoading(true);
      const response = await apiClient(academicHeritageApi);
      const result = await response.json();
      if (response.ok) {
        setData(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching Academic Heritage:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchName = (item.Name || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchTitle = (item.Title || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchOrg = (item.Org || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchName || matchTitle || matchOrg;
    });
  }, [data, searchQuery]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpenId(null);
      }
    };
    const closeDropdown = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 160 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => {
    setFormData(defaultForm);
    setSelectedFile(null);
    setPreviewUrl(null);
    setModalType("add");
    setDropdownOpenId(null);
  };

  const openEditModal = (item) => {
    setFormData({ ...item });
    setSelectedFile(null);
    setPreviewUrl(item.Photo || null);
    setModalType("edit");
    setDropdownOpenId(null);
  };

  const closeModal = () => {
    setModalType("");
    setFormData(defaultForm);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData((prev) => ({ ...prev, Photo: "" }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? academicHeritageApi : `${academicHeritageApi}/${formData._id}`;

      const dataToSend = new FormData();
      dataToSend.append("Name", formData.Name);
      dataToSend.append("Title", formData.Title);
      dataToSend.append("Org", formData.Org);
      dataToSend.append("Display_Order", formData.Display_Order);

      if (selectedFile) {
        dataToSend.append("Photo", selectedFile);
      } else if (formData.Photo) {
        dataToSend.append("Photo", formData.Photo);
      } else {
        dataToSend.append("Photo", "");
      }

      const response = await apiFormDataClient(url, {
        method,
        body: dataToSend
      });

      if (response.ok) {
        fetchHeritageItems();
        closeModal();
      } else {
        const errResult = await response.json();
        alert(errResult.message || "Failed to save academic heritage item.");
      }
    } catch (error) {
      console.error("Error saving academic heritage:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${academicHeritageApi}/${id}`, {
        method: "DELETE"
      });
      if (response.ok) {
        fetchHeritageItems();
        setConfirmId(null);
      }
    } catch (error) {
      console.error("Error deleting academic heritage:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ah-page">
      <div className="ah-header">
        <div>
          <h1>Academic Heritage</h1>
          <p>Manage list of awards, recognitions, institutional milestones, and academic heritage items.</p>
        </div>
        <button type="button" className="ah-add-btn" onClick={openAddModal}>
          <span>+</span> Add Item
        </button>
      </div>

      <div className="ah-filter-card">
        <div className="ah-filter-grid">
          <div className="form-group">
            <label>Search Items</label>
            <input
              type="text"
              placeholder="Search by Name, Title, or Organisation"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="ah-table-card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Title / Recognition</th>
                <th>Organization</th>
                <th>Order</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="table-photo">
                        {item.Photo ? (
                          <img src={item.Photo} alt={item.Name} />
                        ) : (
                          <div className="photo-placeholder">{(item.Name || "?").charAt(0)}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="name-cell">
                        <strong>{item.Name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="title-badge">{item.Title}</span>
                    </td>
                    <td>{item.Org}</td>
                    <td>{item.Display_Order}</td>
                    <td className="actions-cell">
                      <button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, item._id)}>
                        ⋮
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="empty-cell">
                    No academic heritage records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dropdownOpenId !== null && (
        <div
          ref={dropdownRef}
          className="action-dropdown"
          style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}
        >
          <button type="button" onClick={() => openEditModal(data.find((i) => i._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            Edit
          </button>
          <button
            type="button"
            className="delete"
            onClick={() => {
              setConfirmId(dropdownOpenId);
              setDropdownOpenId(null);
            }}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#ef4444" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </div>
            <h3>Delete Record?</h3>
            <p>This will permanently remove the item from the list.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(confirmId)}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="ah-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalType === "add" ? "Add Academic Heritage" : "Edit Academic Heritage"}</h2>
              <button type="button" className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSave}>
              <div className="form-section-label">Media</div>
              <div className="form-group">
                <label>Photo / Award Picture</label>
                <div className="photo-upload-box">
                  {previewUrl ? (
                    <div className="photo-preview-block">
                      <img src={previewUrl} alt="Preview" className="profile-preview" />
                      <div className="photo-preview-actions">
                        <button type="button" className="photo-action-btn" onClick={handleRemovePhoto}>
                          Remove Photo
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="upload-placeholder" htmlFor="heritagePhotoUpload">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Click to upload photo</span>
                    </label>
                  )}
                  <input
                    id="heritagePhotoUpload"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </div>
              </div>

              <div className="form-section-label">Basic Information</div>
              <div className="form-group">
                <label>Name / Awardee / Milestone Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.Name}
                  onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                  placeholder="e.g. Dr. R.J. Singh"
                  required
                />
              </div>

              <div className="form-group">
                <label>Title / Recognition / Award <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.Title}
                  onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                  placeholder="e.g. Life Time Achievement Award"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Organization / Institution</label>
                  <input
                    type="text"
                    value={formData.Org}
                    onChange={(e) => setFormData({ ...formData, Org: e.target.value })}
                    placeholder="e.g. Rajat Group of Colleges"
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.Display_Order}
                    onChange={(e) => setFormData({ ...formData, Display_Order: Number(e.target.value) })}
                    placeholder="e.g. 1"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-light" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicHeritage;
